import { CarrierSchema } from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import { WeightSchema } from "@redotech/redo-fulfillment-outbound-labels-common/util";
import { RateProvider } from "@redotech/redo-fulfillment-rate-common/outbound-labels";
import * as zExt from "@redotech/rpc/ext";
import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export enum CarrierPickupStatus {
  SCHEDULED = "Scheduled",
  CANCELLED = "Cancelled",
}

const PickupAddressSchema_ = z
  .object({
    companyName: z
      .string()
      .nullish()
      .meta({ privacy: privacy.company_name.merchant }),
    contactName: z
      .string()
      .nullish()
      .meta({ privacy: privacy.person_name.merchant }),
    addressLine: z
      .string()
      .nullish()
      .meta({ privacy: privacy.address_line.merchant }),
    room: z.string().nullish().meta({ privacy: privacy.address_line.merchant }),
    floor: z
      .string()
      .nullish()
      .meta({ privacy: privacy.address_line.merchant }),
    city: z.string().nullish().meta({ privacy: privacy.geo.coarse }),
    stateProvince: z.string().nullish().meta({ privacy: privacy.geo.coarse }),
    postalCode: z.string().nullish().meta({ privacy: privacy.geo.coarse }),
    countryCode: z.string().nullish().meta({ privacy: privacy.geo.coarse }),
    phoneNumber: z.string().nullish().meta({ privacy: privacy.phone.merchant }),
    phoneExtension: z
      .string()
      .nullish()
      .meta({ privacy: privacy.text.merchant }),
  })
  .meta({ privacy: privacy.structured });

export const PickupAddressSchema: z.ZodType<PickupAddress> =
  PickupAddressSchema_;
export interface PickupAddress extends z.infer<typeof PickupAddressSchema_> {}

const TimeOfDaySchema_ = z
  .object({
    hour: z.number().min(0).max(23).meta({ privacy: privacy.nonPii }),
    minute: z.number().min(0).max(59).meta({ privacy: privacy.nonPii }),
  })
  .meta({ privacy: privacy.structured });

export const TimeOfDaySchema: z.ZodType<TimeOfDay> = TimeOfDaySchema_;
export interface TimeOfDay extends z.infer<typeof TimeOfDaySchema_> {}

const carrierPickupSchema_ = z
  .object({
    _id: zExt.objectId().meta({ privacy: privacy.id.internal }),
    team: zExt.objectId().meta({ privacy: privacy.id.internal }),
    name: z.string().nullish().meta({ privacy: privacy.resourceLabel }),
    carrier: CarrierSchema,
    provider: createEnum(RateProvider, { enumName: "RateProvider" }).meta({
      privacy: privacy.enumOrStatus,
    }),
    pickupRequestNumber: z.string().meta({ privacy: privacy.id.tracking }),
    pickupDate: z.date().meta({ privacy: privacy.timestamp }),
    readyTime: TimeOfDaySchema.nullish(),
    closeTime: TimeOfDaySchema.nullish(),
    pickupAddress: PickupAddressSchema.nullish(),
    packageCount: z.number().nullish().meta({ privacy: privacy.quantity }),
    totalWeight: WeightSchema.nullish().meta({ privacy: privacy.structured }),
    specialInstructions: z
      .string()
      .nullish()
      .meta({ privacy: privacy.text.merchant }),
    referenceNumber: z
      .string()
      .nullish()
      .meta({ privacy: privacy.id.external.resource }),
    status: createEnum(CarrierPickupStatus, { enumName: "CarrierPickupStatus" })
      .nullish()
      .meta({ privacy: privacy.enumOrStatus }),
    rate: z.string().nullish().meta({ privacy: privacy.money }),
    carrierResponse: z
      .record(z.string(), z.unknown())
      .nullish()
      .meta({ privacy: privacy.untypedPayload }),
    createdAt: z.date().meta({ privacy: privacy.timestamp }),
    updatedAt: z.date().meta({ privacy: privacy.timestamp }),
  })
  .meta({ privacy: privacy.structured });

export const CarrierPickupSchema: z.ZodType<CarrierPickup> =
  carrierPickupSchema_;
export interface CarrierPickup extends z.infer<typeof carrierPickupSchema_> {}
